import type { BooleanState } from '@mfw/components';
import type { LogicalProfile } from '@mfw/common';

import { Avatar, Iconify, Modal, useIntl } from '@mfw/components';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { useMatches } from 'react-router-dom';
import Chip from '@mui/material/Chip';
import Box from '@mui/material/Box';
import React from 'react';

import { useOrganizationContext } from '../../context';
import useLogicalProfiles from '../../hooks/users/useLogicalProfiles';
import { onSurface } from '../../theme/onSurface';
import { useNavigate } from '../../router/hooks';
import messages from './messages';

export interface SwitchProfileModalProps {
  switchProfileModal: BooleanState;
  /**
   * Hides the close icon and disables overlay/Escape dismissal — for the
   * post-login forced pick (`ForcedSwitchProfileModal`), which must be left
   * only by actually selecting a row.
   */
  forced?: boolean;
}

function roleMessage(role: LogicalProfile['role']) {
  switch (role) {
    case 'OWNER':
      return messages.roleOwner;
    case 'TRAINER':
      return messages.roleTrainer;
    case 'CLIENT':
      return messages.roleClient;
    default:
      return undefined;
  }
}

const gridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 2,
} as const;

const tileSx = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: '100%',
  gap: 1,
  p: 1.5,
  borderRadius: 2,
  cursor: 'pointer',
  textAlign: 'center',
  '@media (hover: hover)': {
    '&:hover': {
      bgcolor: 'action.hover',
    },
  },
} as const;

const selectedTileSx = {
  bgcolor: 'action.selected',
} as const;

const tileAvatarSx = {
  width: 88,
  height: 88,
} as const;

const tileNameSx = {
  width: '100%',
} as const;

const addProfileIconSx = {
  ...tileAvatarSx,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 1,
  bgcolor: 'action.hover',
} as const;

/**
 * The account/profile switcher, opened from `SettingsDropdown` (it replaced
 * the sidebar's inline `OrganizationSwitcher`). Lists every organization the
 * account is a member of, and the corp organization when one applies; picking
 * a row switches the active context the same way the old sidebar switcher did.
 */
const SwitchProfileModal = ({
  switchProfileModal,
  forced,
}: SwitchProfileModalProps) => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const matches = useMatches();
  const {
    currentCorpOrganization,
    currentOrganization,
    isCorpMode,
    selectCorpOrganization,
    selectOrganization,
  } = useOrganizationContext();

  // A platform user never opens this modal (`SettingsDropdown` doesn't offer
  // it): the tenant can hold any number of organizations, so they enter one
  // from the admin Organizations list instead.
  const logicalProfiles = useLogicalProfiles(switchProfileModal.isActive);
  const [search, setSearch] = React.useState('');

  const replaceOrganizationPathParam = React.useCallback(
    (organizationId: string) => {
      const orgPathRegex = /\/organizations\/([^/]+)\//;
      const currentMatch = matches.find((match) =>
        orgPathRegex.test(match.pathname),
      );
      if (currentMatch) {
        const newPath = currentMatch.pathname.replace(
          orgPathRegex,
          `/organizations/${organizationId}/`,
        );
        navigate(newPath as any);
      }
    },
    [matches, navigate],
  );

  const handleSelect = React.useCallback(
    (id: string) => {
      switchProfileModal.off();
      if (id === currentCorpOrganization?.id) {
        selectCorpOrganization();
        navigate('/');
        return;
      }
      selectOrganization({ id });
      replaceOrganizationPathParam(id);
    },
    [
      switchProfileModal,
      currentCorpOrganization?.id,
      selectCorpOrganization,
      selectOrganization,
      navigate,
      replaceOrganizationPathParam,
    ],
  );

  const orgProfiles = React.useMemo(() => {
    const result: Array<LogicalProfile & { organizationId: string }> = [];
    (logicalProfiles.data ?? []).forEach((profile) => {
      if (profile.organizationId) {
        result.push({ ...profile, organizationId: profile.organizationId });
      }
    });
    return result;
  }, [logicalProfiles.data]);

  const filteredOrgProfiles = React.useMemo(() => {
    if (!search) return orgProfiles;
    const term = search.toLowerCase();
    return orgProfiles.filter((profile) =>
      (profile.organizationName ?? '').toLowerCase().includes(term),
    );
  }, [orgProfiles, search]);

  const selectedValue = isCorpMode
    ? currentCorpOrganization?.id
    : currentOrganization?.id;

  if (!switchProfileModal.isActive) return null;

  return (
    <Modal
      onClose={switchProfileModal.off}
      disableOverlayClose={forced}
      withoutCloseIcon={forced}
      qa="switch-profile-modal"
      variant="small"
      initiallyOpen
    >
      <Modal.Header>
        <Typography variant="h6">{formatMessage(messages.title)}</Typography>
      </Modal.Header>
      <Box display="flex" flexDirection="column" gap={1.5} pt={1}>
        <TextField
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon="mdi:magnify" size="small" />
              </InputAdornment>
            ),
          }}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={formatMessage(messages.searchPlaceholder)}
          data-testid="switch-profile-modal-search"
          value={search}
          size="small"
          fullWidth
        />
        <Box sx={gridSx}>
          {currentCorpOrganization?.id && (
            <Box
              data-testid="switch-profile-modal-option"
              onClick={() => handleSelect(currentCorpOrganization.id)}
              sx={[
                tileSx,
                selectedValue === currentCorpOrganization.id && selectedTileSx,
              ]}
            >
              <Avatar
                label={currentCorpOrganization.name}
                qa="switch-profile-modal-corp"
                variant="rounded"
                sx={tileAvatarSx}
              />
              <Typography
                variant="body2"
                fontWeight={600}
                sx={tileNameSx}
                noWrap
              >
                {currentCorpOrganization.name}
              </Typography>
              <Chip
                label={formatMessage(messages.corpBadge)}
                color="primary"
                size="small"
              />
            </Box>
          )}
          {filteredOrgProfiles.map((profile) => {
            const message = roleMessage(profile.role);
            return (
              <Box
                data-testid="switch-profile-modal-option"
                onClick={() => handleSelect(profile.organizationId)}
                sx={[
                  tileSx,
                  selectedValue === profile.organizationId && selectedTileSx,
                ]}
                key={profile.organizationId}
              >
                <Box position="relative">
                  <Avatar
                    src={profile.organizationLogoName ?? ''}
                    label={profile.organizationName ?? ''}
                    qa="switch-profile-modal-organization"
                    variant="rounded"
                    sx={tileAvatarSx}
                  />
                  {profile.billingLocked && (
                    <Iconify
                      sx={{
                        position: 'absolute',
                        bottom: -4,
                        right: -4,
                        color: onSurface.error,
                        bgcolor: 'background.paper',
                        borderRadius: '50%',
                      }}
                      icon="mdi:lock-outline"
                      size="small"
                    />
                  )}
                </Box>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={tileNameSx}
                  noWrap
                >
                  {profile.organizationName ?? ''}
                </Typography>
                {profile.billingLocked ? (
                  <Typography color={onSurface.error} variant="caption">
                    {formatMessage(
                      profile.billingLockReason === 'subscription_ended'
                        ? messages.subscriptionEndedBadge
                        : messages.billingLockedBadge,
                    )}
                  </Typography>
                ) : (
                  message && (
                    <Typography color="text.secondary" variant="caption">
                      {formatMessage(message)}
                    </Typography>
                  )
                )}
              </Box>
            );
          })}
          <Box
            onClick={() => {
              switchProfileModal.off();
              navigate('/profiles/new');
            }}
            sx={tileSx}
            data-testid="switch-profile-modal-add-profile"
          >
            <Box sx={addProfileIconSx}>
              <Iconify icon="mdi:plus" size="large" />
            </Box>
            <Typography variant="body2" fontWeight={600}>
              {formatMessage(messages.addProfile)}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default SwitchProfileModal;
